<script lang="ts">
  import { onMount } from "svelte";
  import AuthLayout from "./AuthLayout.svelte";
  import Banner from "$lib/components/Banner.svelte";
  import Button from "$lib/components/Button.svelte";
  import Skeleton from "$lib/components/Skeleton.svelte";
  import {
    approveMcpOAuthRequest,
    denyMcpOAuthRequest,
    getMcpOAuthRequest,
    type McpOAuthBlocker,
    type McpOAuthRequest
  } from "$lib/api/mcp";
  import { AppError, unexpectedError } from "$lib/api/problem";
  import { externalNavigation } from "$lib/auth/externalNavigation";
  import {
    MCP_RESTART_SIGN_IN,
    mcpOAuthBlockerCopy,
    mcpOAuthBlockerFromCode,
    mcpOAuthRedirectLabel,
    mcpOAuthRequestGone,
    mcpOAuthRequestId,
    mcpOAuthScopeLabel,
    type McpOAuthRequestGone
  } from "$lib/domain/mcp-oauth";
  import { formatDateTime } from "$lib/format/dates";
  import { session } from "$lib/stores/session.svelte";
  import { router } from "../../router.svelte";

  // `/mcp/authorize?request=<id>` — the consent half of MCP client sign-in. The portal's
  // `/oauth/<serverId>/authorize` stores the client's request and sends the browser here;
  // approving or denying answers the client's own redirect URI, which is off-origin (a
  // loopback port the client listens on), so the browser leaves through
  // `externalNavigation`, never the SPA router. The request id is not a secret, so it stays
  // in the URL (a reload keeps working); it is never sent to telemetry.

  type Phase = "missing" | "loading" | "ready" | "gone" | "failed" | "returning";

  let phase = $state<Phase>("loading");
  let authRequest = $state<McpOAuthRequest | null>(null);
  let gone = $state<McpOAuthRequestGone>("not_found");
  let loadError = $state<AppError | null>(null);
  let actionError = $state<AppError | null>(null);
  let blocker = $state<McpOAuthBlocker | null>(null);
  let decision = $state<"approved" | "denied" | null>(null);
  let busy = $state<"approve" | "deny" | "reload" | null>(null);

  const requestId = $derived(mcpOAuthRequestId(router.match.query.get("request")));
  const blockerCopy = $derived(
    authRequest && blocker
      ? mcpOAuthBlockerCopy(blocker, {
          canManage: authRequest.seat.canManage,
          orgId: authRequest.organization.id,
          orgName: authRequest.organization.name,
          serverName: authRequest.server.name
        })
      : null
  );

  onMount(() => {
    if (!requestId) {
      phase = "missing";
      return;
    }
    void load(true);
  });

  function fail(caught: unknown): boolean {
    const failure = unexpectedError(caught);
    const kind = mcpOAuthRequestGone(failure);
    if (kind) {
      gone = kind;
      phase = "gone";
      return true;
    }
    return false;
  }

  async function load(initial = false): Promise<void> {
    if (!requestId) return;
    if (initial) phase = "loading";
    else busy = "reload";
    loadError = null;
    actionError = null;
    try {
      const found = await getMcpOAuthRequest(requestId);
      authRequest = found;
      blocker = found.blocker;
      phase = "ready";
    } catch (caught) {
      if (fail(caught)) return;
      if (initial) {
        loadError = unexpectedError(caught);
        phase = "failed";
      } else {
        actionError = unexpectedError(caught);
      }
    } finally {
      busy = null;
    }
  }

  async function decide(kind: "approve" | "deny"): Promise<void> {
    if (!requestId || busy) return;
    busy = kind;
    actionError = null;
    try {
      const result = kind === "approve" ? await approveMcpOAuthRequest(requestId) : await denyMcpOAuthRequest(requestId);
      decision = kind === "approve" ? "approved" : "denied";
      phase = "returning";
      externalNavigation.assign(result.redirectTo);
    } catch (caught) {
      if (fail(caught)) return;
      const failure = unexpectedError(caught);
      const revealed = kind === "approve" ? mcpOAuthBlockerFromCode(failure.code) : null;
      if (revealed) blocker = revealed;
      actionError = failure;
    } finally {
      busy = null;
    }
  }

  function host(uri: string | null): string | null {
    if (!uri) return null;
    try {
      return new URL(uri).host;
    } catch {
      return null;
    }
  }
</script>

<AuthLayout title="Connect an MCP client" subtitle={session.user?.email ? `Signed in as ${session.user.email}` : undefined}>
  <div class="stack">
    {#if phase === "loading"}
      <Skeleton rows={3} height={20} label="Loading the sign-in request" />
    {:else if phase === "missing"}
      <Banner tone="info" title="No sign-in request to show" role="status">
        This page opens when an MCP client asks you to sign in, and the link it opened is incomplete. {MCP_RESTART_SIGN_IN}
      </Banner>
      <Button href="/" variant="ghost" fullWidth>Open customer portal</Button>
    {:else if phase === "gone"}
      {#if gone === "expired"}
        <Banner tone="warn" title="This sign-in request has expired" role="status">
          A sign-in request lasts 10 minutes and can be approved or denied once. {MCP_RESTART_SIGN_IN}
        </Banner>
      {:else}
        <Banner tone="warn" title="This sign-in request was not found" role="status">
          It may belong to an organization that {session.user?.email ?? "this account"} is not a member of, or the link is
          incomplete. {MCP_RESTART_SIGN_IN}
        </Banner>
      {/if}
      <Button href="/" variant="ghost" fullWidth>Open customer portal</Button>
    {:else if phase === "failed" && loadError}
      <Banner tone="error" title="Couldn't load the sign-in request" correlationId={loadError.correlationId}>
        {loadError.userMessage}
        {#snippet actions()}<Button size="sm" variant="ghost" onclick={() => load(true)}>Retry</Button>{/snippet}
      </Banner>
    {:else if phase === "returning" && authRequest}
      {#if decision === "approved"}
        <Banner tone="ok" title="Approved" role="status">
          Returning you to {authRequest.client.name}… If nothing happens, go back to {authRequest.client.name}; you can close this tab.
        </Banner>
      {:else}
        <Banner tone="info" title="Request denied" role="status">
          Returning you to {authRequest.client.name}… It is told that access was denied, and nothing was signed in.
        </Banner>
      {/if}
    {:else if phase === "ready" && authRequest}
      {@const clientHost = host(authRequest.client.uri)}
      <section class="review" aria-labelledby="mcp-authorize-review">
        <h2 id="mcp-authorize-review" class="review-title">
          {authRequest.client.name} wants to connect to {authRequest.server.name} ({authRequest.organization.name}) as {authRequest.subject.email}
        </h2>
        <dl class="facts">
          <div>
            <dt>App</dt>
            <dd>
              {authRequest.client.name}{#if clientHost}<span class="muted"> · {clientHost}</span>{/if}
            </dd>
          </div>
          <div>
            <dt>Returns to</dt>
            <dd><span class="mono redirect">{mcpOAuthRedirectLabel(authRequest.client)}</span></dd>
          </div>
          <div>
            <dt>Access</dt>
            <dd>
              <ul class="scopes">
                {#each authRequest.scopes as scope (scope)}
                  <li>{mcpOAuthScopeLabel(scope)} <span class="mono muted">({scope})</span></li>
                {/each}
              </ul>
            </dd>
          </div>
          <div><dt>MCP seat</dt><dd>{authRequest.seat.status === "active" ? "Active" : "None"}</dd></div>
          <div><dt>Expires</dt><dd>{formatDateTime(authRequest.expiresAt)}</dd></div>
        </dl>
        <p class="copy">
          {#if authRequest.client.redirectLoopback}
            After you decide, your browser returns to {authRequest.client.name} on this computer at
            <span class="mono">{authRequest.client.redirectHost}</span>.
          {:else}
            After you decide, your browser goes to <span class="mono">{authRequest.client.redirectHost}</span>.
          {/if}
          Approve only if you just started this sign-in from {authRequest.client.name}.
          {#if authRequest.client.kind === "dynamic"}The app registered itself with the portal, so its name is not verified.{/if}
        </p>
      </section>

      {#if blockerCopy}
        <Banner tone={blockerCopy.tone} title={blockerCopy.title} role="status">
          {blockerCopy.detail}
          {#snippet actions()}
            {#if blockerCopy.link}
              <a class="banner-link" href={blockerCopy.link.href} target="_blank" rel="noopener">{blockerCopy.link.label}</a>
            {/if}
            {#if blocker !== "mcp_server_revoked"}
              <Button size="sm" variant="ghost" loading={busy === "reload"} onclick={() => load()}>Check again</Button>
            {/if}
          {/snippet}
        </Banner>
      {/if}

      {#if actionError && !mcpOAuthBlockerFromCode(actionError.code)}
        <Banner tone="error" title="We couldn't complete that" correlationId={actionError.correlationId}>{actionError.userMessage}</Banner>
      {/if}

      <Button fullWidth disabled={blocker !== null || busy !== null} loading={busy === "approve"} onclick={() => decide("approve")}>Approve</Button>
      <Button variant="ghost" fullWidth disabled={busy !== null} loading={busy === "deny"} onclick={() => decide("deny")}>Deny</Button>
    {/if}
  </div>
</AuthLayout>

<style>
  .copy {
    margin: 0;
    font-size: 14px;
    line-height: 1.55;
  }
  .review {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  .review-title {
    margin: 0;
    color: var(--heading);
    font-size: 16px;
    font-weight: 650;
    line-height: 1.4;
    overflow-wrap: anywhere;
  }
  .facts {
    display: grid;
    gap: 8px;
    margin: 0;
  }
  .facts div {
    display: grid;
    grid-template-columns: 100px 1fr;
    gap: 10px;
  }
  .facts dt {
    color: var(--muted);
    font-size: 13px;
  }
  .facts dd {
    margin: 0;
    min-width: 0;
    overflow-wrap: anywhere;
    font-size: 13.5px;
  }
  .redirect {
    color: var(--heading);
    font-weight: 600;
  }
  .scopes {
    margin: 0;
    padding: 0;
    list-style: none;
  }
  .banner-link {
    color: var(--accent);
    font-size: 13.5px;
    font-weight: 600;
  }
</style>
